import {
  Box,
  Fade,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import SvgCloseIcon from '~components/icons/close-icon'
import SvgHeadphonesIcon from '~components/icons/headphones-icon'
import SvgSendIcon from '~components/icons/send-icon'
import useBreakpoint from '~hooks/use-breakpoint'
import { COLORS } from '~theme/colors'
import { useChatStream, type ChatMessage } from './use-chat-stream'

const CHATBOT_Z_INDEX = 150000
const CHATBOT_OPEN_KEY = 'chatbot-open'

export default function ChatbotWidget() {
  const { isMobileSize } = useBreakpoint(true)
  const [isOpen, setIsOpen] = useState(() => {
    return sessionStorage.getItem(CHATBOT_OPEN_KEY) === 'true'
  })
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { messages, isStreaming, sendMessage, clearMessages } = useChatStream()

  useEffect(() => {
    sessionStorage.setItem(CHATBOT_OPEN_KEY, String(isOpen))
  }, [isOpen])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, isStreaming])

  useEffect(() => {
    if (isOpen && !isStreaming && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen, isStreaming])

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!inputValue.trim() || isStreaming) return
    sendMessage(inputValue)
    setInputValue('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleNewChat = () => {
    clearMessages()
  }

  return (
    <>
      <Fade in={isOpen} unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 16,
            width: 380,
            maxWidth: 'calc(100vw - 48px)',
            height: 520,
            maxHeight: 'calc(100vh - 150px)',
            bgcolor: COLORS.primary.white,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 36, 61, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: CHATBOT_Z_INDEX,
          }}
        >
          <Box
            sx={{
              bgcolor: COLORS.secondary.darkBlue,
              color: COLORS.primary.white,
              px: 2.5,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SvgHeadphonesIcon size={24} />
              <Typography variant="h3" sx={{ color: COLORS.primary.white }}>
                AIDA Assistant
              </Typography>
            </Stack>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: COLORS.primary.white }}
              aria-label="Close chat"
            >
              <SvgCloseIcon size={20} />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              bgcolor: COLORS.greys.ultraLight,
            }}
          >
            {messages.length === 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: COLORS.greys.dark,
                  gap: 1,
                }}
              >
                <SvgHeadphonesIcon size={48} color={COLORS.secondary.midBlue} />
                <Typography variant="paragraphBold1">
                  Hi! How can I help you?
                </Typography>
                <Typography
                  variant="paragraph2"
                  sx={{ color: COLORS.greys.dark }}
                >
                  Ask me anything about InSite
                </Typography>
              </Box>
            )}

            {messages.map((message) => (
              <ChatMessageBubble key={message.id} message={message} />
            ))}

            {isStreaming && messages[messages.length - 1]?.content === '' && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  p: 1.5,
                  alignSelf: 'flex-start',
                }}
              >
                <TypingIndicator />
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 2,
              borderTop: `1px solid ${COLORS.greys.light}`,
              bgcolor: COLORS.primary.white,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={3}
                size="small"
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isStreaming}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: COLORS.greys.ultraLight,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.secondary.midBlue,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.primary.maerskBlue,
                    },
                  },
                }}
              />
              <IconButton
                type="submit"
                disabled={!inputValue.trim() || isStreaming}
                sx={{
                  bgcolor: COLORS.primary.maerskBlue,
                  color: COLORS.primary.white,
                  '&:hover': {
                    bgcolor: COLORS.secondary.hoverBlue,
                  },
                  '&.Mui-disabled': {
                    bgcolor: COLORS.greys.mid,
                    color: COLORS.primary.white,
                  },
                }}
                aria-label="Send message"
              >
                <SvgSendIcon size={20} />
              </IconButton>
            </Stack>

            {messages.length > 0 && (
              <Typography
                variant="caption"
                component="button"
                onClick={handleNewChat}
                sx={{
                  mt: 1,
                  color: COLORS.secondary.midBlue,
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: COLORS.primary.maerskBlue,
                  },
                }}
              >
                Start new conversation
              </Typography>
            )}
          </Box>
        </Box>
      </Fade>

      <IconButton
        onClick={() => setIsOpen((prev) => !prev)}
        sx={{
          position: 'fixed',
          bottom: isMobileSize ? 8 : 32,
          right: 16,
          width: isMobileSize ? 36 : 56,
          height: isMobileSize ? 36 : 56,
          bgcolor: COLORS.secondary.deepBlue,
          color: COLORS.primary.white,
          borderRadius: isMobileSize ? '8px' : '16px',
          zIndex: CHATBOT_Z_INDEX,
          '&:hover': {
            bgcolor: COLORS.secondary.midBlue,
          },
        }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? <SvgCloseIcon size={20} /> : <SvgHeadphonesIcon size={20} />}
      </IconButton>
    </>
  )
}

function ChatMessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user'

  return (
    <Box
      sx={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '85%',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: isUser ? COLORS.primary.maerskBlue : COLORS.primary.white,
          color: isUser ? COLORS.primary.white : COLORS.secondary.darkBlue,
          boxShadow: isUser ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.08)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        <Typography
          variant="paragraph2"
          component="div"
          sx={{ color: 'inherit', lineHeight: 1.6 }}
        >
          <MessageContent content={message.content} isUser={isUser} />
        </Typography>
      </Box>
    </Box>
  )
}

function MessageContent({
  content,
  isUser,
}: {
  content: string
  isUser: boolean
}) {
  // Remove citation markers like [doc1], [doc2], etc.
  const cleanedContent = content.replace(/\[doc\d+\]/g, '')

  const urlPattern = /(https?:\/\/[^\s]+)|(\/[a-zA-Z0-9\-/_?=&]+)/g

  // Check if there are any URLs in the content
  if (!urlPattern.test(cleanedContent)) {
    return <>{cleanedContent}</>
  }

  // Reset regex after test
  urlPattern.lastIndex = 0

  const elements: { key: string; node: React.ReactNode }[] = []
  let lastIndex = 0

  const matches = cleanedContent.matchAll(urlPattern)

  for (const match of matches) {
    if (match.index !== undefined && match.index > lastIndex) {
      const text = cleanedContent.slice(lastIndex, match.index)
      elements.push({ key: `text-${lastIndex}`, node: text })
    }

    const url = match[0]
    const matchIndex = match.index ?? lastIndex
    const isExternal = url.startsWith('http')

    if (isExternal) {
      elements.push({
        key: `url-${matchIndex}`,
        node: (
          <Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: isUser ? COLORS.primary.white : COLORS.primary.maerskBlue,
              textDecoration: 'underline',
              '&:hover': {
                color: isUser
                  ? COLORS.secondary.lightBlue
                  : COLORS.secondary.hoverBlue,
              },
            }}
          >
            {url}
          </Link>
        ),
      })
    } else {
      elements.push({
        key: `url-${matchIndex}`,
        node: (
          <Link
            component={RouterLink}
            to={url}
            sx={{
              color: isUser ? COLORS.primary.white : COLORS.primary.maerskBlue,
              textDecoration: 'underline',
              '&:hover': {
                color: isUser
                  ? COLORS.secondary.lightBlue
                  : COLORS.secondary.hoverBlue,
              },
            }}
          >
            {url}
          </Link>
        ),
      })
    }

    lastIndex = matchIndex + url.length
  }

  if (lastIndex < cleanedContent.length) {
    const text = cleanedContent.slice(lastIndex)
    elements.push({ key: `text-${lastIndex}`, node: text })
  }

  return (
    <>
      {elements.map((element) => (
        <Fragment key={element.key}>{element.node}</Fragment>
      ))}
    </>
  )
}

function TypingIndicator() {
  const dots = [
    { id: 'dot-1', delay: 0 },
    { id: 'dot-2', delay: 0.16 },
    { id: 'dot-3', delay: 0.32 },
  ]

  return (
    <Box sx={{ display: 'flex', gap: 0.5, p: 1 }}>
      {dots.map((dot) => (
        <Box
          key={dot.id}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: COLORS.greys.mid,
            animation: 'bounce 1.4s infinite ease-in-out both',
            animationDelay: `${dot.delay}s`,
            '@keyframes bounce': {
              '0%, 80%, 100%': {
                transform: 'scale(0)',
              },
              '40%': {
                transform: 'scale(1)',
              },
            },
          }}
        />
      ))}
    </Box>
  )
}
